import React from 'react';
import { Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { GridPanel, dataGridDefaults } from '../../../components/list-grid-panel';
import { RowAction } from '../../../components/row-action';
import BudgetActionAppService from '../../../application/platform/sge/budget-action/budget-action-app-service';
import { useArrayList } from '../../../hooks/use-array-list';
import { formatCodeName } from '../components/code-name-label';
import { useBudgetActionSearchController } from './context/budget-action-search-controller';
import { useBudgetActionSearchState } from './context/budget-action-search-context';
import { getBudgetActionTypeLabel } from './components/budget-action-form-utils';
import { BudgetActionDrawer } from './components/budget-action-drawer';

export function BudgetActionSearchComponent() {
    const ctrl = useBudgetActionSearchController();
    const { parameters, budgetPrograms, budgetUnits, searchTrigger, drawer } = useBudgetActionSearchState();
    const { rows, loading } = useArrayList(BudgetActionAppService.list, parameters, searchTrigger, 'Não foi possível carregar as ações orçamentárias');

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80 },
        { field: 'code', headerName: 'Código', width: 140 },
        { field: 'name', headerName: 'Nome', flex: 1, minWidth: 200 },
        {
            field: 'budgetProgramId', headerName: 'Programa Orçamentário', width: 240,
            valueGetter: (params) => budgetPrograms.find((p) => p.id === params.value)?.name || params.value
        },
        { field: 'actionType', headerName: 'Tipo', width: 120, valueGetter: (params) => getBudgetActionTypeLabel(params.value) },
        {
            field: 'actions', headerName: 'Ações', width: 100, sortable: false,
            renderCell: (params) => (
                <React.Fragment>
                    <RowAction title="Editar" onClick={() => ctrl.openDrawer(params.row)}>
                        <EditIcon fontSize="small" />
                    </RowAction>
                    <RowAction title="Excluir" color="error" onClick={() => ctrl.handleDelete(params.row)}>
                        <DeleteIcon fontSize="small" />
                    </RowAction>
                </React.Fragment>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Ações Orçamentárias"
                    subtitle="Gerencie as ações orçamentárias cadastradas"
                    breadcrumbs={[{ label: 'Ações Orçamentárias' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch}>
                    <Grid item xs={12} md={8}>
                        <InputLabel>Buscar por código ou nome</InputLabel>
                        <OutlinedInput fullWidth name="input-search" value={parameters.search || ''} onChange={ctrl.handleOnChange} onKeyDown={(e) => e.key === 'Enter' && ctrl.handleSearch()} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <InputLabel>Programa Orçamentário</InputLabel>
                        <Select fullWidth name="select-budget-program" value={parameters.budgetProgramId ?? ''} onChange={ctrl.handleOnChange}>
                            <MenuItem value=""><em>Todos</em></MenuItem>
                            {budgetPrograms.map((p) => <MenuItem key={p.id} value={p.id}>{formatCodeName(p)}</MenuItem>)}
                        </Select>
                    </Grid>
                </FilterPanel>
            </Grid>
            <Grid item xs={12}>
                <GridPanel addLabel="Nova ação orçamentária" onAdd={() => ctrl.openDrawer()}>
                    <DataGrid
                        {...dataGridDefaults}
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                    />
                </GridPanel>
            </Grid>

            <BudgetActionDrawer
                open={drawer.open}
                initial={drawer.record}
                initialBudgetUnitId={drawer.budgetUnitId}
                budgetUnits={budgetUnits}
                isSaving={ctrl.isSaving}
                onClose={ctrl.closeDrawer}
                onSave={ctrl.handleSave}
            />
        </React.Fragment>
    );
}
